import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Plus,
  Trash2,
  Maximize2,
  Minimize2,
  LayoutGrid,
  Wallet,
  ArrowLeftRight,
  Zap,
  Bell,
  BarChart3,
  Clock,
  Users,
  TrendingUp,
} from 'lucide-react';

type WidgetSize = 'SMALL' | 'MEDIUM' | 'LARGE' | 'FULL';

interface WidgetConfig {
  widgetId: string;
  position: {
    row: number;
    col: number;
    rowSpan?: number;
    colSpan?: number;
  };
  size?: WidgetSize;
  settings?: Record<string, unknown>;
}

interface WidgetDefinition {
  code: string;
  name: string;
  description: string;
  icon: string;
  defaultSize: WidgetSize;
}

interface WidgetConfiguratorProps {
  widgets: WidgetConfig[];
  availableWidgets: WidgetDefinition[];
  onChange: (widgets: WidgetConfig[]) => void;
  isLoading?: boolean;
}

const WIDGET_ICONS: Record<string, React.ElementType> = {
  'balance-summary': Wallet,
  'recent-transactions': ArrowLeftRight,
  'quick-actions': Zap,
  'notifications': Bell,
  'balance-chart': BarChart3,
  'pending-approvals': Clock,
  'tenant-stats': Users,
  'shortcuts': LayoutGrid,
  'performance': TrendingUp,
};

const SIZE_CONFIG: Record<WidgetSize, { label: string; colSpan: number }> = {
  SMALL: { label: 'Pequeno', colSpan: 3 },
  MEDIUM: { label: 'Médio', colSpan: 4 },
  LARGE: { label: 'Grande', colSpan: 6 },
  FULL: { label: 'Largura Total', colSpan: 12 },
};

function SortableWidget({
  widget,
  definition,
  onRemove,
  onResize,
}: {
  widget: WidgetConfig;
  definition?: WidgetDefinition;
  onRemove: () => void;
  onResize: (size: WidgetSize) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.widgetId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = WIDGET_ICONS[widget.widgetId] || LayoutGrid;
  const size = widget.size || definition?.defaultSize || 'MEDIUM';
  const colSpan = SIZE_CONFIG[size].colSpan;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        gridColumn: `span ${colSpan}`,
      }}
      className={`
        bg-surface-700 rounded-lg p-4 border border-surface-600
        ${isDragging ? 'ring-2 ring-primary-500' : ''}
      `}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <button
            {...attributes}
            {...listeners}
            className="p-1 text-surface-400 hover:text-white cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <div className="p-2 bg-primary-500/20 rounded-lg">
            <Icon className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h4 className="font-medium text-white text-sm">
              {definition?.name || widget.widgetId}
            </h4>
            <p className="text-xs text-surface-400">
              {definition?.description || 'Widget personalizado'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <select
            value={size}
            onChange={(e) => onResize(e.target.value as WidgetSize)}
            className="text-xs bg-surface-600 border border-surface-500 rounded px-2 py-1 text-white"
          >
            {Object.entries(SIZE_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </select>
          <button
            onClick={onRemove}
            className="p-1 text-surface-400 hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function WidgetLibraryItem({
  definition,
  onAdd,
  isAdded,
}: {
  definition: WidgetDefinition;
  onAdd: () => void;
  isAdded: boolean;
}) {
  const Icon = WIDGET_ICONS[definition.code] || LayoutGrid;

  return (
    <button
      onClick={onAdd}
      disabled={isAdded}
      className={`
        flex items-center gap-3 p-3 rounded-lg border transition-all text-left w-full
        ${isAdded
          ? 'bg-surface-800 border-surface-700 opacity-50 cursor-not-allowed'
          : 'bg-surface-700 border-surface-600 hover:border-primary-500 hover:bg-surface-600'
        }
      `}
    >
      <div className={`p-2 rounded-lg ${isAdded ? 'bg-surface-600' : 'bg-primary-500/20'}`}>
        <Icon className={`w-4 h-4 ${isAdded ? 'text-surface-400' : 'text-primary-400'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-white text-sm truncate">{definition.name}</div>
        <div className="text-xs text-surface-400 truncate">{definition.description}</div>
      </div>
      {!isAdded && (
        <Plus className="w-4 h-4 text-surface-400" />
      )}
    </button>
  );
}

export function WidgetConfigurator({
  widgets,
  availableWidgets,
  onChange,
  isLoading,
}: WidgetConfiguratorProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = widgets.findIndex((w) => w.widgetId === active.id);
      const newIndex = widgets.findIndex((w) => w.widgetId === over.id);
      
      const newWidgets = arrayMove(widgets, oldIndex, newIndex).map((w, index) => ({
        ...w,
        position: {
          ...w.position,
          row: Math.floor(index / 3),
          col: (index % 3) * 4,
        },
      }));
      
      onChange(newWidgets);
    }
  };

  const handleAddWidget = (definition: WidgetDefinition) => {
    const newWidget: WidgetConfig = {
      widgetId: definition.code,
      position: {
        row: Math.floor(widgets.length / 3),
        col: (widgets.length % 3) * 4,
        colSpan: SIZE_CONFIG[definition.defaultSize].colSpan,
        rowSpan: 1,
      },
      size: definition.defaultSize,
    };
    onChange([...widgets, newWidget]);
  };

  const handleRemoveWidget = (widgetId: string) => {
    onChange(widgets.filter((w) => w.widgetId !== widgetId));
  };

  const handleResizeWidget = (widgetId: string, size: WidgetSize) => {
    onChange(
      widgets.map((w) =>
        w.widgetId === widgetId
          ? {
              ...w,
              size,
              position: {
                ...w.position,
                colSpan: SIZE_CONFIG[size].colSpan,
              },
            }
          : w
      )
    );
  };

  const activeWidget = activeId ? widgets.find((w) => w.widgetId === activeId) : null;
  const activeDefinition = activeId
    ? availableWidgets.find((d) => d.code === activeId)
    : null;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-surface-700 rounded w-1/4" />
        <div className="grid grid-cols-12 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-span-4 h-24 bg-surface-700 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Configuração de Widgets</h3>
          <p className="text-sm text-surface-400">
            Arraste para reordenar, adicione ou remova widgets do dashboard
          </p>
        </div>
        <div className="text-sm text-surface-400">
          {widgets.length} widget{widgets.length !== 1 ? 's' : ''} configurado{widgets.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Widget Grid */}
        <div className="lg:col-span-3">
          <div className="bg-surface-800 rounded-lg p-4 min-h-[300px]">
            {widgets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-surface-400">
                <LayoutGrid className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-center">
                  Nenhum widget configurado.<br />
                  Adicione widgets da biblioteca ao lado.
                </p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={widgets.map((w) => w.widgetId)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-12 gap-4">
                    {widgets.map((widget) => (
                      <SortableWidget
                        key={widget.widgetId}
                        widget={widget}
                        definition={availableWidgets.find((d) => d.code === widget.widgetId)}
                        onRemove={() => handleRemoveWidget(widget.widgetId)}
                        onResize={(size) => handleResizeWidget(widget.widgetId, size)}
                      />
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeWidget && activeDefinition && (
                    <div className="bg-surface-700 rounded-lg p-4 border-2 border-primary-500 shadow-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary-500/20 rounded-lg">
                          {(() => {
                            const Icon = WIDGET_ICONS[activeWidget.widgetId] || LayoutGrid;
                            return <Icon className="w-5 h-5 text-primary-400" />;
                          })()}
                        </div>
                        <div>
                          <h4 className="font-medium text-white text-sm">
                            {activeDefinition.name}
                          </h4>
                        </div>
                      </div>
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>

        {/* Widget Library */}
        <div className="lg:col-span-1">
          <div className="bg-surface-800 rounded-lg p-4">
            <h4 className="font-medium text-white mb-4">Biblioteca de Widgets</h4>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {availableWidgets.map((definition) => (
                <WidgetLibraryItem
                  key={definition.code}
                  definition={definition}
                  onAdd={() => handleAddWidget(definition)}
                  isAdded={widgets.some((w) => w.widgetId === definition.code)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WidgetConfigurator;
